import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { FieldWrap, NativeSelect } from "@/components/Field";
import { Composer } from "@/components/messages/Composer";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

const MODES = [["user", "One person"], ["churches", "Selected churches"], ["all", "Everyone"]];

function ChurchPicker({ churches, ids, setIds, admins, adminIds, setAdminIds }) {
  const all = ids.length === churches.length && churches.length > 0;
  const toggle = (id) => setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  const toggleAdmin = (id) => setAdminIds(adminIds.includes(id) ? adminIds.filter((x) => x !== id) : [...adminIds, id]);
  return (
    <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-3">
      {admins.length > 0 && (
        <div className="border-b border-slate-100 pb-2" data-testid="recipient-admins">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Admins</div>
          {admins.map((a) => (
            <label key={a.id} className="flex items-center gap-2 py-1 text-sm"><Checkbox checked={adminIds.includes(a.id)} onCheckedChange={() => toggleAdmin(a.id)} data-testid={`recipient-admin-${a.id}`} /> {a.name} <span className="text-xs text-slate-500">({a.title})</span></label>
          ))}
        </div>
      )}
      <label className="flex items-center gap-2 border-b border-slate-100 pb-2 text-sm font-semibold"><Checkbox checked={all} onCheckedChange={() => setIds(all ? [] : churches.map((c) => c.id))} data-testid="recipient-church-all" /> Select all churches</label>
      {churches.map((c) => (
        <label key={c.id} className="flex items-center gap-2 py-1 text-sm"><Checkbox checked={ids.includes(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`recipient-church-${c.id}`} /> {c.name}</label>
      ))}
    </div>
  );
}

export function NewMessageDialog({ open, onClose, onSent }) {
  const { churches, settings } = useApp();
  const msg = settings?.messaging || {};
  const [mode, setMode] = useState("user");
  const [userId, setUserId] = useState("");
  const [ids, setIds] = useState([]);
  const [adminIds, setAdminIds] = useState([]);
  const [people, setPeople] = useState([]);
  useEffect(() => { if (open) { setMode("user"); setUserId(""); setIds([]); setAdminIds([]); api.get("/messages/recipients").then((r) => setPeople(r.data)).catch(() => {}); } }, [open]);
  const cname = (id) => churches.find((c) => c.id === id)?.name || "No church";
  const admins = people.filter((p) => p.is_admin);
  const users = people.filter((p) => !p.is_admin);
  const oneList = [...(msg.admins_in_one !== false ? admins : []), ...users];
  const allCount = users.length + (msg.admins_in_all !== false ? admins.length : 0);

  const send = async (body, attachments) => {
    const r = await api.post("/messages", { to: mode, user_id: userId || null, church_ids: ids, admin_ids: msg.admins_in_groups !== false ? adminIds : [], body, attachments });
    toast.success(`Message sent to ${r.data.sent} ${r.data.sent === 1 ? "person" : "people"}`);
    onSent();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6"><DialogTitle>New message</DialogTitle></DialogHeader>
        <div className="space-y-4 px-6">
          <FieldWrap label="Send to">
            <div className="flex flex-wrap gap-2">
              {MODES.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setMode(k)} data-testid={`recipient-mode-${k}`}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${mode === k ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"}`}>{l}</button>
              ))}
            </div>
          </FieldWrap>
          {mode === "user" && <NativeSelect value={userId} onChange={setUserId} placeholder="Choose a person..." testid="recipient-user-select" options={oneList.map((p) => ({ value: p.id, label: p.is_admin ? `${p.name} — ${p.title}` : `${p.name} — ${cname(p.church_id)}${p.title ? ` (${p.title})` : ""}` }))} />}
          {mode === "churches" && <ChurchPicker churches={churches} ids={ids} setIds={setIds} admins={msg.admins_in_groups !== false ? admins : []} adminIds={adminIds} setAdminIds={setAdminIds} />}
          {mode === "all" && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="recipient-all-note">This goes to {allCount} {allCount === 1 ? "person" : "people"}: {users.length} church user{users.length === 1 ? "" : "s"}{msg.admins_in_all !== false && admins.length ? ` and ${admins.length} admin${admins.length === 1 ? "" : "s"}` : ""}.</p>}
        </div>
        <div className="mt-4"><Composer key={String(open)} onSend={send} testid="new-message-composer" /></div>
      </DialogContent>
    </Dialog>
  );
}
